import { createApp } from 'vue'
import { createPinia } from 'pinia'

import './style.css'
import 'katex/dist/katex.min.css'
import 'vue-sonner/style.css'
import App from './App.vue'
import ConfigError from './components/ConfigError.vue'
import router from './router'
import { loadConfig } from './lib/load-config'
import { loadTheme } from './lib/load-theme'
import { applyTheme } from './lib/apply-theme'
import { useConfigStore } from './stores/config'
import { useUserStore } from './modules/user/stores/useUserStore'
import { isAuthMocked } from './lib/env'
import { initSentry } from './lib/sentry'
import { initPosthog, trackSecurity } from './lib/posthog'

// // Waits for the Clerk SDK to load, then — if there is an existing session —
// // additionally waits for the JWT to be available. This ensures the app only
// // mounts after Clerk has finished its full post-auth initialization, so no
// // delayed routerReplace redirects are pending when the router starts.
// async function waitForClerkReady(): Promise<void> {
//   await new Promise<void>((resolve) => {
//     if (window.Clerk?.loaded) { resolve(); return }
//     const id = setInterval(() => {
//       if (window.Clerk?.loaded) { clearInterval(id); resolve() }
//     }, 50)
//   })

//   if (!window.Clerk!.user) return

//   await new Promise<void>((resolve) => {
//     const poll = async () => {
//       try {
//         const token = await window.Clerk!.session?.getToken()
//         if (token) { resolve(); return }
//       } catch { /* session not ready yet */ }
//       setTimeout(poll, 50)
//     }
//     poll()
//   })
// }

async function bootstrap() {
  try {
    const results = await Promise.allSettled([loadConfig(), loadTheme()])

    const configResult = results[0]
    const themeResult = results[1]

    if (configResult.status === 'rejected') throw configResult.reason
    if (themeResult.status === 'rejected') throw themeResult.reason

    const config = configResult.value
    const theme = themeResult.value

    const app = createApp(App)
    initSentry(app, router)
    initPosthog()
    const pinia = createPinia()
    app.use(pinia)

    const configStore = useConfigStore()
    configStore.initialize(config)

    applyTheme(theme)

    document.title = config.branding.appName

    const favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
    if (favicon) favicon.href = config.branding.faviconPath

    if (!isAuthMocked) {
      const { clerkPlugin, clerkOptions } = await import('./plugins/clerk')
      app.use(clerkPlugin, clerkOptions)
      // await waitForClerkReady()
    }

    if (isAuthMocked) {
      configStore.fetchSeasons() // fire-and-forget
      const userStore = useUserStore()
      userStore.initialize('local-user') // fire-and-forget
    }

    app.use(router)
    app.mount('#app')

    if (navigator.webdriver) {
      console.warn('[security] Automated browser detected (navigator.webdriver)')
      trackSecurity('automated_browser_detected')
    }
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err)
    createApp(ConfigError, { message }).mount('#app')
  }
}

bootstrap()
